/**
 * Bringing an existing camera estate into Trinetra, three ways: a spreadsheet, a
 * database dump from the incumbent VMS, or an API the customer already exposes.
 *
 * The CSV path is genuinely live in the browser — the file is parsed, columns are
 * mapped and rows are validated client-side, so a customer can drop their real
 * inventory in and watch it come out the other side. The dump and API paths stage a
 * representative preview instead; neither can work without a server.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import StatusTag from '../common/StatusTag';
import { BTN_PRIMARY, BTN_SECONDARY, SELECTED_CARD } from '../common/ui';
import { Callout, Field, inputClass, monoInputClass } from './ui';
import { parseCsv } from '../../utils/parseCsv';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  CircleStackIcon,
  CloudArrowDownIcon,
  DocumentTextIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

type Method = 'file' | 'dump' | 'api';

/** Trinetra's own camera fields, and which spreadsheet headers usually carry them. */
const TARGET_FIELDS = [
  { key: 'name', label: 'Camera name', required: true, hints: ['name', 'camera', 'title', 'description'] },
  { key: 'rtsp_url', label: 'Stream URL', required: true, hints: ['rtsp', 'url', 'stream', 'uri'] },
  { key: 'ip_address', label: 'IP address', required: false, hints: ['ip', 'address', 'host'] },
  { key: 'location', label: 'Location', required: false, hints: ['location', 'site', 'zone', 'area'] },
  { key: 'group', label: 'Group', required: false, hints: ['group', 'folder', 'building'] },
  { key: 'manufacturer', label: 'Manufacturer', required: false, hints: ['make', 'vendor', 'manufacturer', 'brand'] },
];

const UNMAPPED = '__none__';

/** Best-guess header → Trinetra field, so a clean file needs no mapping at all. */
function autoMap(headers: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const f of TARGET_FIELDS) {
    const found = headers.find((h) => {
      const lower = h.toLowerCase();
      return f.hints.some((hint) => lower.includes(hint));
    });
    map[f.key] = found && !Object.values(map).includes(found) ? found : UNMAPPED;
  }
  return map;
}

const SAMPLE_API_ROWS = [
  { name: 'Gate 1 — Entry', rtsp_url: 'rtsp://10.20.4.11:554/Streaming/Channels/101', location: 'North Gate', group: 'Perimeter' },
  { name: 'Gate 1 — Exit', rtsp_url: 'rtsp://10.20.4.12:554/Streaming/Channels/101', location: 'North Gate', group: 'Perimeter' },
  { name: 'Lobby Reception', rtsp_url: 'rtsp://10.20.5.31:554/cam/realmonitor?channel=1', location: 'Tower A', group: 'Common Areas' },
  { name: 'Warehouse Bay 3', rtsp_url: 'rtsp://10.20.7.19:554/live/ch0', location: 'Warehouse', group: 'Operations' },
];

interface Props {
  canEdit: boolean;
  /**
   * Where this app's REAL camera importer lives, if it has one.
   *
   * The local node has `/cameras/import`; the cloud adds cameras through a node and has no
   * equivalent screen. Passing the path is what lets one component say "the real thing is over
   * here" in the app that has it, and stay quiet in the app that does not — rather than
   * offering a dead link to half its users.
   */
  bulkUploadPath?: string;
}

export default function CameraImport({ canEdit, bulkUploadPath }: Props) {
  const [method, setMethod] = useState<Method>('file');

  // ── Spreadsheet ──
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // ── Database dump ──
  const dumpInput = useRef<HTMLInputElement>(null);
  const [dumpName, setDumpName] = useState('');
  const [dumpEngine, setDumpEngine] = useState('postgres');
  const [dumpScanning, setDumpScanning] = useState(false);
  const [dumpResult, setDumpResult] = useState<{ table: string; rows: number; match: string }[] | null>(null);

  // ── API endpoint ──
  const [apiUrl, setApiUrl] = useState('');
  const [apiAuth, setApiAuth] = useState('bearer');
  const [apiToken, setApiToken] = useState('');
  const [apiPath, setApiPath] = useState('data.cameras');
  const [apiSchedule, setApiSchedule] = useState('1h');
  const [apiFetching, setApiFetching] = useState(false);
  const [apiRows, setApiRows] = useState<typeof SAMPLE_API_ROWS | null>(null);

  const [imported, setImported] = useState<number | null>(null);

  const mappedRows = useMemo(() => {
    if (!headers.length) return [];
    const idx: Record<string, number> = {};
    for (const f of TARGET_FIELDS) {
      const h = mapping[f.key];
      idx[f.key] = h && h !== UNMAPPED ? headers.indexOf(h) : -1;
    }
    return rows.map((r) => {
      const out: Record<string, string> = {};
      for (const f of TARGET_FIELDS) out[f.key] = idx[f.key] >= 0 ? (r[idx[f.key]] || '').trim() : '';
      return out;
    });
  }, [headers, rows, mapping]);

  const validRows = useMemo(
    () => mappedRows.filter((r) => TARGET_FIELDS.every((f) => !f.required || r[f.key])),
    [mappedRows],
  );
  const invalidCount = mappedRows.length - validRows.length;

  function handleFile(file: File) {
    setFileError('');
    setImported(null);
    setFileName(file.name);

    // .xlsx is a zip of XML — converting it in the browser needs a parser we don't ship.
    // The real importer converts it server-side; here we say so rather than fail silently.
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      setHeaders([]);
      setRows([]);
      setFileError(
        'This preview reads CSV only. Excel workbooks are supported by the real importer — '
        + 'save this one as CSV to stage it here.',
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result || ''));
      if (parsed.length < 2) {
        setFileError('That file has no data rows under its header.');
        setHeaders([]);
        setRows([]);
        return;
      }
      const head = parsed[0].map((h) => h.trim());
      setHeaders(head);
      setRows(parsed.slice(1));
      setMapping(autoMap(head));
    };
    reader.onerror = () => setFileError('Could not read that file.');
    reader.readAsText(file);
  }

  function scanDump() {
    setDumpScanning(true);
    setDumpResult(null);
    window.setTimeout(() => {
      setDumpScanning(false);
      setDumpResult([
        { table: 'devices', rows: 128, match: 'Cameras — name, ip, model' },
        { table: 'device_streams', rows: 256, match: 'Stream URLs — main and sub profiles' },
        { table: 'device_groups', rows: 14, match: 'Groups — maps onto Trinetra groups' },
        { table: 'users', rows: 37, match: 'Not imported — Trinetra keeps its own accounts' },
      ]);
    }, 1300);
  }

  function fetchApi() {
    setApiFetching(true);
    setApiRows(null);
    window.setTimeout(() => {
      setApiFetching(false);
      setApiRows(SAMPLE_API_ROWS);
    }, 1200);
  }

  const importCount =
    method === 'file' ? validRows.length
      : method === 'dump' ? (dumpResult ? 128 : 0)
      : (apiRows?.length ?? 0);

  return (
    <div className="space-y-4">
      {/* Method picker */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {([
          { key: 'file', icon: TableCellsIcon, title: 'Spreadsheet', body: 'A CSV or Excel export of the existing camera list.' },
          { key: 'dump', icon: CircleStackIcon, title: 'Database dump', body: 'A backup from the VMS the customer runs today.' },
          { key: 'api', icon: CloudArrowDownIcon, title: 'API endpoint', body: 'An endpoint Trinetra polls to stay in step.' },
        ] as const).map((m) => {
          const active = method === m.key;
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              onClick={() => setMethod(m.key)}
              className={`rounded-xl border p-4 text-left shadow-card transition-colors ${
                active
                  ? SELECTED_CARD
                  : 'border-border bg-card hover:border-brand hover:bg-muted'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                    active ? 'bg-brand text-white' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                {active && <CheckCircleIcon className="h-4 w-4 text-brand" />}
              </div>
              <p className="mt-3 text-[13px] font-semibold text-foreground">{m.title}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{m.body}</p>
            </button>
          );
        })}
      </div>

      {/*
        ── Spreadsheet ──

        WHERE THE APP HAS A REAL IMPORTER, THIS HANDS OVER TO IT.

        This panel used to stage rows into component state and offer an "Import" button that did
        nothing, because the whole page is a preview of VMS integration. Someone who opened the
        Camera Import tab to import cameras — which is exactly what the tab is called — uploaded
        a workbook, got told to convert it, and was then stuck in front of a button that would
        have done nothing anyway. A preview is allowed to be a preview; it is not allowed to be
        mistaken for the working feature sitting one click away.
      */}
      {method === 'file' && bulkUploadPath && (
        <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
          <div>
            <h3 className="text-[14px] font-semibold text-foreground">Upload camera list</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              This one is built and working — it lives on its own page rather than in this preview.
            </p>
          </div>

          <Callout tone="success">
            Bulk camera upload takes a .csv or an .xlsx straight into this node: it reads the
            sheet, maps the columns, creates each camera exactly as the Add Camera form would, and
            reports a verdict per row against your own line numbers.
          </Callout>

          <Link
            to={bulkUploadPath}
            className={BTN_PRIMARY}
          >
            <TableCellsIcon className="h-4 w-4" /> Open bulk camera upload
          </Link>

          <p className="border-t border-border pt-4 text-[11px] leading-relaxed text-muted-foreground">
            The two methods beside it — a VMS database dump, and an endpoint Trinetra polls — are
            previews of how an existing estate would be adopted. Neither is wired to a connector
            yet.
          </p>
        </div>
      )}

      {/* The preview itself, for an app with no importer of its own to defer to. */}
      {method === 'file' && !bulkUploadPath && (
        <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
          <div>
            <h3 className="text-[14px] font-semibold text-foreground">Upload camera list</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Any column order works — Trinetra guesses the mapping and you correct it below.
            </p>
          </div>


          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />

          <button
            onClick={() => fileInput.current?.click()}
            disabled={!canEdit}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-4 py-8 transition-colors hover:border-brand hover:bg-muted disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="h-6 w-6 text-muted-foreground" />
            <span className="text-[13px] font-medium text-foreground">
              {fileName || 'Choose a CSV or Excel file'}
            </span>
            <span className="text-[11px] text-muted-foreground">.csv, .xlsx, .xls</span>
          </button>

          {fileError && <Callout tone="info">{fileError}</Callout>}

          {headers.length > 0 && (
            <>
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="text-[12px] font-medium text-foreground">Column mapping</p>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {TARGET_FIELDS.map((f) => (
                    <Field key={f.key} label={f.label} required={f.required}>
                      <select
                        value={mapping[f.key] ?? UNMAPPED}
                        onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                        className={inputClass}
                      >
                        <option value={UNMAPPED}>— not mapped —</option>
                        {headers.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <StatusTag tone="success">{validRows.length} ready to import</StatusTag>
                {invalidCount > 0 && (
                  <StatusTag tone="warning">{invalidCount} missing a name or stream URL</StatusTag>
                )}
                <StatusTag tone="neutral">{headers.length} columns detected</StatusTag>
              </div>

              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-left text-[12px]">
                  <thead className="bg-muted">
                    <tr>
                      {TARGET_FIELDS.map((f) => (
                        <th key={f.key} className="whitespace-nowrap px-3 py-2 font-medium text-muted-foreground">
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mappedRows.slice(0, 8).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        {TARGET_FIELDS.map((f) => (
                          <td
                            key={f.key}
                            className="max-w-[220px] truncate px-3 py-2 text-foreground"
                            title={r[f.key]}
                          >
                            {r[f.key] || <span className="text-muted-foreground">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {mappedRows.length > 8 && (
                <p className="text-[11px] text-muted-foreground">
                  Showing 8 of {mappedRows.length} rows.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Database dump ── */}
      {method === 'dump' && (
        <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
          <div>
            <h3 className="text-[14px] font-semibold text-foreground">Import from a VMS database dump</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Upload a backup of the existing system. Trinetra reads the device tables and leaves
              everything else alone — accounts and permissions stay in Trinetra.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Database engine">
              <select
                value={dumpEngine}
                onChange={(e) => setDumpEngine(e.target.value)}
                className={inputClass}
              >
                <option value="postgres">PostgreSQL (.sql / .dump)</option>
                <option value="mysql">MySQL / MariaDB (.sql)</option>
                <option value="mssql">SQL Server (.bak)</option>
                <option value="sqlite">SQLite (.db)</option>
              </select>
            </Field>
            <Field label="Dump file">
              <input
                ref={dumpInput}
                type="file"
                accept=".sql,.dump,.bak,.db,.gz,.zip"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setDumpName(f.name); setDumpResult(null); }
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => dumpInput.current?.click()}
                disabled={!canEdit}
                className="flex w-full items-center gap-2 rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
              >
                <DocumentTextIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{dumpName || 'Choose a dump file…'}</span>
              </button>
            </Field>
          </div>

          <button
            onClick={scanDump}
            disabled={!canEdit || !dumpName || dumpScanning}
            className={BTN_SECONDARY}
          >
            {dumpScanning ? (
              <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Analysing dump…</>
            ) : (
              <><CircleStackIcon className="h-4 w-4" /> Analyse dump</>
            )}
          </button>

          {dumpResult && (
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-left text-[12px]">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Table</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Rows</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Recognised as</th>
                  </tr>
                </thead>
                <tbody>
                  {dumpResult.map((t) => (
                    <tr key={t.table} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-foreground">{t.table}</td>
                      <td className="px-3 py-2 tabular-nums text-foreground">{t.rows}</td>
                      <td className="px-3 py-2 text-muted-foreground">{t.match}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── API endpoint ── */}
      {method === 'api' && (
        <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
          <div>
            <h3 className="text-[14px] font-semibold text-foreground">Pull cameras from an API</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Point Trinetra at any endpoint that returns a camera list. It re-reads on a
              schedule, so cameras added in the other system appear here on their own.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Endpoint URL" required className="sm:col-span-2">
              <input
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://vms.customer.local/api/v2/cameras"
                className={monoInputClass}
              />
            </Field>
            <Field label="Re-read every">
              <select
                value={apiSchedule}
                onChange={(e) => setApiSchedule(e.target.value)}
                className={inputClass}
              >
                <option value="manual">Manual only</option>
                <option value="15m">15 minutes</option>
                <option value="1h">Hour</option>
                <option value="24h">Day</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Authentication">
              <select
                value={apiAuth}
                onChange={(e) => setApiAuth(e.target.value)}
                className={inputClass}
              >
                <option value="none">None</option>
                <option value="basic">Basic auth</option>
                <option value="bearer">Bearer token</option>
                <option value="apikey">API key header</option>
              </select>
            </Field>
            <Field label={apiAuth === 'basic' ? 'user:password' : 'Token / key'}>
              <input
                type="password"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                disabled={apiAuth === 'none'}
                autoComplete="new-password"
                className={inputClass}
              />
            </Field>
            <Field label="List path in response" hint="Where the camera array sits in the JSON.">
              <input
                value={apiPath}
                onChange={(e) => setApiPath(e.target.value)}
                placeholder="data.cameras"
                className={monoInputClass}
              />
            </Field>
          </div>

          <button
            onClick={fetchApi}
            disabled={!canEdit || !apiUrl.trim() || apiFetching}
            className={BTN_SECONDARY}
          >
            {apiFetching ? (
              <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Reading endpoint…</>
            ) : (
              <><CloudArrowDownIcon className="h-4 w-4" /> Fetch preview</>
            )}
          </button>

          {apiRows && (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-[12px]">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Name</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Stream URL</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Location</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Group</th>
                  </tr>
                </thead>
                <tbody>
                  {apiRows.map((r) => (
                    <tr key={r.name} className="border-t border-border">
                      <td className="px-3 py-2 text-foreground">{r.name}</td>
                      <td className="max-w-[280px] truncate px-3 py-2 font-mono text-muted-foreground" title={r.rtsp_url}>
                        {r.rtsp_url}
                      </td>
                      <td className="px-3 py-2 text-foreground">{r.location}</td>
                      <td className="px-3 py-2 text-foreground">{r.group}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Import action — shared by all three methods */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 shadow-card">
        <div>
          <p className="text-[13px] font-medium text-foreground">
            {importCount > 0 ? `${importCount} cameras ready` : 'Nothing staged yet'}
          </p>
          <p className="text-[11px] text-muted-foreground">
            Imported cameras arrive disabled, so nothing starts streaming until they are reviewed.
          </p>
        </div>
        <button
          onClick={() => setImported(importCount)}
          disabled={!canEdit || importCount === 0}
          className={BTN_PRIMARY}
        >
          Import {importCount > 0 ? importCount : ''} cameras
        </button>
      </div>

      {imported != null && imported > 0 && (
        <Callout tone="success">
          {imported} cameras staged for import. They appear on the Cameras page, disabled, once
          the import connector is enabled for this node.
        </Callout>
      )}

    </div>
  );
}
